import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar } from "@mui/material";
import {
  GridActionsCellItem,
  GridPaginationModel,
  GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { AccountCircle } from "@mui/icons-material";
import {
  AppSyncSearchQuery,
  AppSyncSearchResult,
} from "@commr/admin/models/common/appSyncSearch";

import { CircleMemberRow } from "@commr/admin/models/user/circleMember/circleMember";
import { useParams } from "react-router-dom";
import { addCircleMemberToResident } from "@commr/admin/services/residentService";
import { setUserPicture } from "@commr/admin/utils/userUtils";
import CustomDataGrid from "../common/customDataGrid";

interface Props {
  searchResult?: AppSyncSearchResult;
  searchQuery: AppSyncSearchQuery;
  setSearchQuery: Dispatch<SetStateAction<AppSyncSearchQuery>>;
}

const CircleMemberHit: React.FC<React.PropsWithChildren<Props>> = ({
  searchResult,
  searchQuery,
  setSearchQuery,
}) => {
  const { tenantId, residencyId, residentId } = useParams<{
    tenantId: string;
    residencyId: string;
    residentId: string;
  }>();
  const { t } = useTranslation();
  const [circleMembersRows, setCircleMembersRows] = useState<CircleMemberRow[]>(
    []
  );

  const setAvatar = (params: GridRenderCellParams) => (
    <Avatar src={params.value?.toString()} />
  );
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: searchQuery.limit,
  });

  const handleAddCircleMember = (circleMemberId: string) => {
    addCircleMemberToResident(
      residentId,
      residencyId,
      tenantId,
      circleMemberId
    ).then(() => {
      window.location.reload();
    });
  };
  const CircleMembersColumns: any = [
    {
      field: "avatar",
      headerName: "",
      flex: 0.1,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<any>) => setAvatar(params),
      renderHeader: () => <AccountCircle />,
    },
    {
      field: "lastName",
      headerName: t("common:lastName"),
      flex: 1,
    },
    {
      field: "firstName",
      headerName: t("common:firstName"),
      flex: 1,
    },
    {
      field: "email",
      headerName: t("common:email"),
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Action",
      type: "actions",
      flex: 0.3,
      align: "center",
      headerAlign: "center",
      getActions: (params: any) => [
        <GridActionsCellItem
          label={t("common:add")}
          onClick={() => handleAddCircleMember(params.id)}
          showInMenu
        />,
      ],
    },
  ];

  useEffect(() => {
    if (searchResult) {
      setCircleMembersRows([]);
      searchResult.items?.map(async (hit) => {
        const avatar = await setUserPicture(hit.profilePicture);
        const circleMember: CircleMemberRow = {
          id: hit.id,
          avatar,
          lastName: hit.lastName,
          firstName: hit.firstName,
          email: hit.email,
          role: hit.role,
          relation: hit.relation,
        };
        setCircleMembersRows((dataGridRows) => [...dataGridRows, circleMember]);
      });
    }
  }, [searchResult]);

  const handlePaginationModelChange = (
    newPaginationModel: GridPaginationModel
  ) => {
    setPaginationModel(newPaginationModel);
    setSearchQuery((old) => ({
      ...old,
      from: newPaginationModel.page * searchQuery.limit!,
    }));
  };

  const gridProps = {
    pagination: true,
    rowCount: searchResult?.total ?? 0,
    onPaginationModelChange: handlePaginationModelChange,
    paginationModel,
    pageSizeOptions: [],
    rows: circleMembersRows,
    loading: !searchResult,
    columns: CircleMembersColumns,
    paginationMode: "server",
    disableColumnMenu: true,
    disableSelectionOnClick: true,
    disableColumnSelector: true,
    initialState: {
      sorting: {
        sortModel: [{ field: "createdAt", sort: "asc" }],
      },
    },
  };

  return (
    <div style={{ height: 650, width: "100%" }}>
      <CustomDataGrid attribute={gridProps} />
    </div>
  );
};

export default CircleMemberHit;

CircleMemberHit.defaultProps = {
  searchResult: undefined,
};
